import { getInviteRoute, ROUTES } from "../constants/routes";
import { loadAccountRole, needsOnboarding } from "../integrations/supabase/domain";
import { writeActorRoleHint } from "./actorRoleHint";

export const buildOnboardingPath = (inviteToken?: string | null) =>
  inviteToken
    ? `${ROUTES.onboarding}?inviteToken=${encodeURIComponent(inviteToken)}`
    : ROUTES.onboarding;

/**
 * Where a freshly signed-in user lands.
 *
 * - With an invite token: instructors go back to the invite page (`/invite/:token`), which is the
 *   only place a chart-sharing invite can be accepted (and which forwards to instructor setup if
 *   their profile is missing). Everyone else keeps the existing spouse flow: onboarding with the
 *   token, which offers accept/skip at the end.
 * - Otherwise: onboarding if needed, the roster for instructors, Observations for everyone else.
 *   The role lookup runs in parallel with the onboarding check, so nobody waits longer.
 *
 * Also primes the role hint so the shell's first paint already shows the right nav. A failed role
 * lookup (null) is never guessed at: no hint, and the non-instructor route.
 */
export const resolvePostSignInRoute = async (
  userId: string,
  { inviteToken }: { inviteToken?: string | null } = {}
): Promise<{ route: string; mustOnboard: boolean | null }> => {
  if (inviteToken) {
    const role = await loadAccountRole(userId).catch(() => null);
    if (role) {
      writeActorRoleHint({ userId, role });
    }
    return {
      route: role === "instructor" ? getInviteRoute(inviteToken) : buildOnboardingPath(inviteToken),
      mustOnboard: null,
    };
  }

  const [mustOnboard, role] = await Promise.all([
    needsOnboarding(userId),
    loadAccountRole(userId).catch(() => null),
  ]);

  if (role) {
    writeActorRoleHint({ userId, role });
  }

  const route = mustOnboard
    ? ROUTES.onboarding
    : role === "instructor"
      ? ROUTES.instructorClients
      : ROUTES.observations;

  return { route, mustOnboard };
};
